import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Plus, X } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    emptyScanTimeEntry,
    initialScanTimeEntries,
    totalSeconds,
    type ScanTimeEntry,
} from '../../utils/scanTime';

const fields: { key: keyof ScanTimeEntry; unit: string }[] = [
    { key: 'hours', unit: 'h' },
    { key: 'minutes', unit: 'min' },
    { key: 'seconds', unit: 's' },
];

type Props = {
    entries: ScanTimeEntry[];
    setEntries: (entries: ScanTimeEntry[]) => void;
    onClose: () => void;
};

export default function ScanTimeDialog({
    entries,
    setEntries,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const listRef = useRef<HTMLDivElement>(null);
    const total = totalSeconds(entries);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (value: number) => String(value).padStart(2, '0');

    function updateEntry(index: number, change: Partial<ScanTimeEntry>) {
        setEntries(
            entries.map((entry, i) =>
                i === index ? { ...entry, ...change } : entry
            )
        );
    }

    function removeEntry(index: number) {
        const remaining = entries.filter((_, i) => i !== index);
        setEntries(remaining.length > 0 ? remaining : [emptyScanTimeEntry]);
    }

    function addEntry() {
        setEntries([...entries, emptyScanTimeEntry]);
        requestAnimationFrame(() => {
            listRef.current?.lastElementChild?.querySelector('input')?.focus();
        });
    }

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent className="flex max-h-[90vh] w-[94vw] flex-col gap-6 overflow-hidden rounded-2xl p-4 sm:p-6">
                <DialogHeader className="text-left">
                    <DialogTitle>{t('scanTimeTracker.title')}</DialogTitle>
                    <DialogDescription className="font-medium text-subtle-foreground">
                        {t('scanTimeTracker.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="shrink-0 rounded-xl border bg-muted px-5 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-medium text-subtle-foreground">
                            {t('scanTimeTracker.total')}
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEntries(initialScanTimeEntries)}
                        >
                            {t('scanTimeTracker.clearAll')}
                        </Button>
                    </div>
                    <p className="mt-1 text-3xl font-semibold tabular-nums tracking-tight">
                        {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                    </p>
                </div>

                <div className="flex min-h-0 flex-1 flex-col gap-2">
                    <div
                        ref={listRef}
                        className="-mx-1 flex min-h-0 flex-col gap-2 overflow-y-auto p-1"
                    >
                        {entries.map((entry, index) => (
                            <div
                                key={index}
                                className="flex shrink-0 items-center gap-2"
                            >
                                <span className="w-5 shrink-0 text-sm font-medium tabular-nums text-subtle-foreground">
                                    {index + 1}
                                </span>
                                {fields.map((field) => (
                                    <div
                                        key={field.key}
                                        className="relative min-w-0 flex-1"
                                    >
                                        <Input
                                            inputMode="numeric"
                                            aria-label={t(
                                                'scanTimeTracker.fieldLabel',
                                                {
                                                    field: t(
                                                        `scanTimeTracker.fields.${field.key}`
                                                    ),
                                                    number: index + 1,
                                                }
                                            )}
                                            value={entry[field.key]}
                                            onChange={(event) =>
                                                updateEntry(index, {
                                                    [field.key]:
                                                        event.target.value.replace(
                                                            /\D/g,
                                                            ''
                                                        ),
                                                })
                                            }
                                            className="h-9 pl-3 pr-9 font-medium tabular-nums"
                                        />
                                        <span
                                            aria-hidden="true"
                                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-subtle-foreground"
                                        >
                                            {field.unit}
                                        </span>
                                    </div>
                                ))}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t(
                                        'scanTimeTracker.removeTime',
                                        { number: index + 1 }
                                    )}
                                    onClick={() => removeEntry(index)}
                                    className="size-8 shrink-0 rounded-full text-subtle-foreground"
                                >
                                    <X aria-hidden="true" />
                                </Button>
                            </div>
                        ))}
                    </div>

                    <div className="shrink-0 pl-7">
                        <Button onClick={addEntry}>
                            <Plus aria-hidden="true" />
                            {t('scanTimeTracker.addTime')}
                        </Button>
                    </div>
                </div>

                <div className="flex shrink-0 gap-2.5 text-xs font-medium leading-relaxed text-subtle-foreground">
                    <Info
                        aria-hidden="true"
                        className="mt-0.5 size-3.5 shrink-0"
                    />
                    <p>{t('scanTimeTracker.hint')}</p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
